import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import OwlCarousel from "react-owl-carousel3";
import "owl.carousel/dist/assets/owl.carousel.css";
import "owl.carousel/dist/assets/owl.theme.default.css";
import ApiCall from "../common/ApiCall";
import { getAuthToken } from "../common/Helper";
import ToastAlert from "../common/ToastAlert";
import defaultAvatar from "../assets/images/profile-avatar/default-avatar.png";

const LeftSection = ({ activeForm }) => {
  const [sliderItems, setSliderItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCarousel, setShowCarousel] = useState(false);

  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 991);

  // Settings states loaded from database
  const [showVideo, setShowVideo] = useState(false);
  const [videoUrl, setVideoUrl] = useState("");
  const [videoAutoStart, setVideoAutoStart] = useState(false);
  const [showSlider, setShowSlider] = useState(true);
  const [sliderAutoplay, setSliderAutoplay] = useState(true);
  const [sliderTimer, setSliderTimer] = useState(5);
  const [selectedItemIndex, setSelectedItemIndex] = useState(null);

  const [showTitle1, setShowTitle1] = useState(true);
  const [title1, setTitle1] = useState("");
  const [subtitle1, setSubtitle1] = useState("");

  const [showTitle2, setShowTitle2] = useState(false);
  const [title2, setTitle2] = useState("");
  const [subtitle2, setSubtitle2] = useState("");

  const [titleLoaded, setTitleLoaded] = useState(false);

  const DEFAULT_TITLE = "COMPARE YOUR DREAMS";
  const DEFAULT_SUBTITLE = "with others throughout the world.";

  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth < 991);
    };

    handleResize(); // Initialize on mount
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const carouselOptions = {
    loop: sliderItems.length > 1,
    margin: 10,
    dots: false,
    nav: true,
    autoplay: sliderAutoplay,
    autoplayTimeout: (sliderTimer || 5) * 1000,
    smartSpeed: 800,
    animateIn: "slideInRightToLeft",
    animateOut: "slideOutLeftToRight",
    navText: ["", "<i class='fa-solid fa-chevron-right'></i>"],
    responsive: {
      0: {
        items: 1,
      },
      600: {
        items: 1,
      },
      1000: {
        items: 1,
      },
    },
  };

  const truncateText = (text) => {
    const limit = isSmallScreen ? 20 : 25;
    if (!text) return "";

    const words = text.split(" ");
    return words.length > limit
      ? words.slice(0, limit).join(" ") + "..."
      : text;
  };

  const getEmbedUrl = (url, autostart = false) => {
    if (!url) return null;
    const ytMatch = url.match(
      /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/,
    );
    if (ytMatch && ytMatch[1]) {
      const autoParam = autostart ? "autoplay=1&mute=1" : "autoplay=0";
      return `https://www.youtube.com/embed/${ytMatch[1]}?${autoParam}&loop=1&playlist=${ytMatch[1]}`;
    }
    const vimeoMatch = url.match(
      /(?:vimeo\.com\/|player\.vimeo\.com\/video\/)([0-9]+)/,
    );
    if (vimeoMatch && vimeoMatch[1]) {
      const autoParam = autostart ? "autoplay=1&muted=1" : "autoplay=0";
      return `https://player.vimeo.com/video/${vimeoMatch[1]}?${autoParam}&loop=1`;
    }
    return null;
  };

  const fetchFrontpageSettings = async () => {
    try {
      const response = await ApiCall(
        "GET",
        "frontpage-content?type=frontpage_title,frontpage_video,slider",
        null,
        null,
      );
      if (response?.data?.success && response.data.data.contents) {
        const contents = response.data.data.contents;

        // Title settings
        const titleContent = contents.find((c) => c.type === "frontpage_title");
        if (titleContent) {
          try {
            const parsed = JSON.parse(titleContent.text);
            if (parsed.title1 !== undefined || parsed.title2 !== undefined) {
              setTitle1((parsed.title1 || DEFAULT_TITLE).trim());
              setSubtitle1((parsed.subtitle1 || DEFAULT_SUBTITLE).trim());
              setShowTitle1(
                parsed.status1 !== undefined
                  ? parsed.status1 === 1
                  : titleContent.status === 1,
              );

              setTitle2((parsed.title2 || "").trim());
              setSubtitle2((parsed.subtitle2 || "").trim());
              setShowTitle2(
                parsed.status2 !== undefined ? parsed.status2 === 1 : false,
              );
            } else {
              // Legacy format fallback
              setTitle1((parsed.title || DEFAULT_TITLE).trim());
              setSubtitle1((parsed.subtitle || DEFAULT_SUBTITLE).trim());
              setShowTitle1(titleContent.status === 1);

              setTitle2("");
              setSubtitle2("");
              setShowTitle2(false);
            }
          } catch (e) {
            setTitle1((titleContent.text || DEFAULT_TITLE).trim());
            setSubtitle1(DEFAULT_SUBTITLE.trim());
            setShowTitle1(titleContent.status === 1);

            setTitle2("");
            setSubtitle2("");
            setShowTitle2(false);
          }
        } else {
          setShowTitle1(true);
          setTitle1(DEFAULT_TITLE);
          setSubtitle1(DEFAULT_SUBTITLE);
          setShowTitle2(false);
          setTitle2("");
          setSubtitle2("");
        }

        // Video settings
        const videoContent = contents.find((c) => c.type === "frontpage_video");
        if (videoContent) {
          setShowVideo(videoContent.status === 1);
          try {
            const parsed = JSON.parse(videoContent.text);
            if (
              typeof parsed === "object" &&
              parsed !== null &&
              "url" in parsed
            ) {
              setVideoUrl(parsed.url || "");
              setVideoAutoStart(Boolean(parsed.autostart));
            } else {
              setVideoUrl(videoContent.text || "");
              setVideoAutoStart(false);
            }
          } catch (e) {
            setVideoUrl(videoContent.text || "");
            setVideoAutoStart(false);
          }
        } else {
          setShowVideo(false);
          setVideoUrl("");
          setVideoAutoStart(false);
        }

        // Slider settings
        const sliderContent = contents.find((c) => c.type === "slider");
        let localSliderType = "custom";
        let localFeedId = "verified";
        let localDisplayOrder = "chronological";
        let localAutoplay = true;
        let localTimer = 5;

        if (sliderContent) {
          setShowSlider(sliderContent.status === 1);
          try {
            const parsed = JSON.parse(sliderContent.text);
            localSliderType = parsed.slider_type || "custom";
            localFeedId = parsed.feed_id || "verified";
            localDisplayOrder = parsed.display_order || "chronological";
            localAutoplay =
              parsed.autoplay !== undefined ? Boolean(parsed.autoplay) : true;
            localTimer =
              parsed.transition_timer !== undefined
                ? Number(parsed.transition_timer)
                : 5;
          } catch (e) {
            localSliderType = "custom";
          }
        } else {
          setShowSlider(true);
        }
        setSliderAutoplay(localAutoplay);
        setSliderTimer(localTimer);

        // Load correct slider items based on type and status
        if (sliderContent && sliderContent.status === 1) {
          if (localSliderType === "feed") {
            await fetchFeedSlider(localFeedId, localDisplayOrder);
          } else {
            await fetchCustomSlider();
          }
        } else if (!sliderContent) {
          await fetchCustomSlider();
        } else {
          setLoading(false);
        }
      } else {
        // Fallbacks if no database contents
        setShowTitle1(true);
        setTitle1(DEFAULT_TITLE);
        setSubtitle1(DEFAULT_SUBTITLE);
        setShowTitle2(false);
        setTitle2("");
        setSubtitle2("");
        setShowVideo(false);
        setShowSlider(true);
        await fetchCustomSlider();
      }
    } catch (error) {
      console.error("Error fetching frontpage settings:", error);
      setShowTitle1(true);
      setTitle1(DEFAULT_TITLE);
      setSubtitle1(DEFAULT_SUBTITLE);
      setShowTitle2(false);
      setTitle2("");
      setSubtitle2("");
      setShowVideo(false);
      setShowSlider(true);
      await fetchCustomSlider();
    } finally {
      setTitleLoaded(true);
    }
  };

  const fetchCustomSlider = async () => {
    try {
      const response = await ApiCall(
        "GET",
        "slider/list?type=active",
        {},
        null,
      );
      if (response?.data?.success) {
        const data = response.data.data || [];
        setSliderItems(data);
        setShowCarousel(false);
        setTimeout(() => setShowCarousel(true), 50);
      }
    } catch (error) {
      console.error("Error fetching custom slider:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFeedSlider = async (feedId, displayOrder) => {
    try {
      const response = await ApiCall(
        "GET",
        `public-feed?feed=${feedId}&order=${displayOrder}`,
        {},
        null,
      );
      if (response?.data?.success) {
        const data = response.data.data || [];
        if (data.length > 0) {
          setSliderItems(data);
          setShowCarousel(false);
          setTimeout(() => setShowCarousel(true), 50);
          setLoading(false);
        } else {
          await fetchCustomSlider();
        }
      } else {
        await fetchCustomSlider();
      }
    } catch (error) {
      console.error("Error fetching feed slider:", error);
      await fetchCustomSlider();
    }
  };

  useEffect(() => {
    fetchFrontpageSettings();
  }, []);

  const videoBoxStyle = {
    width: "100%",
    maxWidth: "480px",
    aspectRatio: "16 / 9",
    borderRadius: "12px",
    overflow: "hidden",
    boxShadow: "0 8px 24px rgba(0,0,0,0.3)",
    background: "#000",
    marginBottom: "25px",
  };

  const iframeStyle = {
    width: "100%",
    height: "100%",
    border: "none",
  };

  useEffect(() => {
    if (selectedItemIndex === null) return;

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setSelectedItemIndex(null);
      } else if (e.key === "ArrowLeft") {
        setSelectedItemIndex((prev) =>
          prev !== null ? Math.max(0, prev - 1) : null,
        );
      } else if (e.key === "ArrowRight") {
        setSelectedItemIndex((prev) =>
          prev !== null ? Math.min(sliderItems.length - 1, prev + 1) : null,
        );
      }
    };

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [selectedItemIndex, sliderItems.length]);

  return (
    <div className="left_section">
      {/* Frontpage Title 1 and Subtitle 1 (Above Video) */}
      {showTitle1 && titleLoaded && (title1 || subtitle1) && (
        <div
          className="title_box title_box_1"
          style={{
            width: "100%",
            maxWidth: "480px",
            textAlign: "center",
            marginBottom: "15px",
          }}
        >
          {title1 && (
            <h1 style={{ textAlign: "center", margin: 0, width: "100%" }}>
              {title1}
            </h1>
          )}
          {subtitle1 && (
            <p style={{ textAlign: "center", margin: 0, width: "100%" }}>
              {subtitle1}
            </p>
          )}
        </div>
      )}

      {/* Video Container */}
      {showVideo && getEmbedUrl(videoUrl, videoAutoStart) && (
        <div style={videoBoxStyle}>
          <iframe
            src={getEmbedUrl(videoUrl, videoAutoStart)}
            title="Frontpage Video"
            style={iframeStyle}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          ></iframe>
        </div>
      )}

      {/* Frontpage Title 2 and Subtitle 2 (Under Video / Above Slider).
          Tied to the slideshow it introduces: hidden whenever the slider is,
          including the register-on-small-screen case below. Widths <=767px are
          handled by the .title_box_2 CSS rule alongside .slider_box. */}
      {showTitle2 &&
        titleLoaded &&
        (title2 || subtitle2) &&
        showSlider &&
        !(activeForm === "register" && isSmallScreen) && (
          <div
            className="title_box title_box_2"
            style={{
              width: "100%",
              maxWidth: "480px",
              textAlign: "center",
              marginBottom: "15px",
            }}
          >
            {title2 && (
              <h1 style={{ textAlign: "center", margin: 0, width: "100%" }}>
                {title2}
              </h1>
            )}
            {subtitle2 && (
              <p style={{ textAlign: "center", margin: 0, width: "100%" }}>
                {subtitle2}
              </p>
            )}
          </div>
        )}
      {showSlider && !(activeForm === "register" && isSmallScreen) && (
        <div className="slider_box">
          {loading ? (
            <p className="text-center py-4">Loading sliders...</p>
          ) : sliderItems.length > 0 ? (
            showCarousel && (
              <OwlCarousel className="owl-theme" {...carouselOptions}>
                {sliderItems.map((item, index) => (
                  <div
                    className="item"
                    key={index}
                    onClick={() => setSelectedItemIndex(index)}
                    style={{ cursor: "pointer" }}
                    title="Click to view full dream"
                  >
                    <div className="img_box">
                      <img
                        src={
                          item.image_url ||
                          "/assets/images/profile-avatar/default-avatar.png"
                        }
                        alt={
                          item.user?.name ||
                          item.name ||
                          item.title ||
                          `slider${index + 1}`
                        }
                      />
                      {(item.name || item.title) && (
                        <div className="slider-name-overlay">
                          {item.name || item.title}
                        </div>
                      )}
                    </div>
                    <div className="text_slider">
                      <p>
                        {truncateText(
                          item.text ||
                            item.description ||
                            item.dream_description,
                        )}
                      </p>
                    </div>
                    <div className="slider-click-hint" aria-hidden="true">
                      (Click to see more)
                    </div>
                  </div>
                ))}
              </OwlCarousel>
            )
          ) : (
            <p className="text-center py-4">No sliders available</p>
          )}
        </div>
      )}

      {/* Dream Slideshow Full View Modal rendered via Portal to document.body */}
      {selectedItemIndex !== null &&
        sliderItems[selectedItemIndex] &&
        createPortal(
          (() => {
            const item = sliderItems[selectedItemIndex];
            const avatarSrc =
              item.image_url ||
              item.user_avatar ||
              item.user?.avatar ||
              item.user?.avatar_url ||
              item.avatar ||
              item.avatar_url ||
              defaultAvatar;
            const personName =
              item.user?.name || item.name || item.title || "Dream";
            const dreamText =
              item.text ||
              item.description ||
              item.dream_description ||
              "No description available.";

            return (
              <div
                className="slider-dream-modal-backdrop"
                onClick={() => setSelectedItemIndex(null)}
              >
                <div
                  className="slider-dream-modal-box"
                  onClick={(e) => e.stopPropagation()}
                >
                  {/* Header row with title left-aligned and close button top-right */}
                  <div className="slider-dream-modal-header">
                    <h4 className="slider-dream-modal-name">{personName}</h4>
                    <button
                      type="button"
                      className="slider-dream-modal-close"
                      onClick={() => setSelectedItemIndex(null)}
                      aria-label="Close"
                      title="Close (Esc)"
                    >
                      <i className="fa-solid fa-xmark"></i>
                    </button>
                  </div>

                  {/* Person avatar — large circle */}
                  <div className="slider-dream-modal-avatar-wrapper">
                    <div className="slider-dream-modal-avatar">
                      <img
                        src={avatarSrc}
                        alt={personName}
                        onError={(e) => {
                          e.target.src = defaultAvatar;
                        }}
                      />
                    </div>
                  </div>

                  {/* Full dream text */}
                  <div className="slider-dream-modal-body">
                    <p className="slider-dream-modal-text">{dreamText}</p>
                  </div>

                  {/* Navigation Footer */}
                  <div className="slider-dream-modal-footer">
                    <button
                      type="button"
                      className="slider-dream-modal-nav-btn"
                      onClick={() =>
                        setSelectedItemIndex((prev) => Math.max(0, prev - 1))
                      }
                      disabled={
                        selectedItemIndex === 0 || sliderItems.length <= 1
                      }
                    >
                      <i className="fa-solid fa-chevron-left me-1"></i> Previous
                    </button>
                    <span className="slider-dream-modal-counter">
                      {selectedItemIndex + 1} of {sliderItems.length}
                    </span>
                    <button
                      type="button"
                      className="slider-dream-modal-nav-btn"
                      onClick={() =>
                        setSelectedItemIndex((prev) =>
                          Math.min(sliderItems.length - 1, prev + 1),
                        )
                      }
                      disabled={
                        selectedItemIndex === sliderItems.length - 1 ||
                        sliderItems.length <= 1
                      }
                    >
                      Next <i className="fa-solid fa-chevron-right ms-1"></i>
                    </button>
                  </div>
                </div>
              </div>
            );
          })(),
          document.body,
        )}
    </div>
  );
};

export default LeftSection;
